import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch, BASE_URL } from "./api";
import { formatSeconds } from "./WorkoutTracker";

const TYPE_OPTIONS = [
  { value: "erg", label: "Erg" },
  { value: "water", label: "On the water" },
  { value: "run", label: "Run" },
  { value: "lift", label: "Lift" },
  { value: "other", label: "Other" },
];

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

const MAX_FETCH_LIMIT = 50;

const DEFAULT_FORM = {
  performedOn: todayStr(),
  workoutType: "erg",
  description: "",
  durationMinutes: "",
  distanceMeters: "",
};

export default function AthleteOyoWorkouts({ user, limit = 20 }) {
  const userId = user?.id ? Number(user.id) : null;
  const fetchLimit = Math.max(1, Math.min(Number(limit) || 20, MAX_FETCH_LIMIT));
  const [form, setForm] = useState(() => ({ ...DEFAULT_FORM }));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [photoState, setPhotoState] = useState({ preview: "", data: "", type: "" });
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [formModalOpen, setFormModalOpen] = useState(false);

  const typeLabelByValue = useMemo(() => {
    const map = new Map();
    TYPE_OPTIONS.forEach((opt) => map.set(opt.value, opt.label));
    return map;
  }, []);

  const loadEntries = useCallback(async () => {
    if (!userId) {
      setEntries([]);
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [oyoData, teamData] = await Promise.all([
        apiFetch(`/oyo-workouts/mine?limit=${fetchLimit}&as=${userId}`),
        apiFetch(`/workout-results/mine?limit=${fetchLimit}&as=${userId}`),
      ]);
      const normalizedOyo = normalizeOyoEntries(Array.isArray(oyoData) ? oyoData : []);
      const normalizedTeam = normalizeTeamResults(Array.isArray(teamData) ? teamData : []);
      const combined = sortOyoEntries([...normalizedOyo, ...normalizedTeam]).slice(0, fetchLimit);
      setEntries(combined);
    } catch (e) {
      console.error("Failed to load OYO workouts", e);
      setEntries([]);
      setError(e?.message || "Failed to load workouts");
    } finally {
      setLoading(false);
    }
  }, [userId, fetchLimit]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const updateForm = (updates) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  const resetForm = () => {
    setForm({
      performedOn: todayStr(),
      workoutType: "erg",
      description: "",
      durationMinutes: "",
      distanceMeters: "",
    });
    clearPhoto();
    setEditingId(null);
    setError("");
    setSuccess("");
  };

  const clearPhoto = () => {
    setPhotoState({ preview: "", data: "", type: "" });
    setPhotoInputKey((prev) => prev + 1);
  };

  const handlePhotoChange = (event) => {
    setError("");
    setSuccess("");
    const file = event.target.files && event.target.files[0];
    if (!file) {
      clearPhoto();
      return;
    }
    if (!ALLOWED_PHOTO_TYPES.has(file.type)) {
      setError("Please choose a JPG, PNG, GIF, or WebP image.");
      event.target.value = "";
      clearPhoto();
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setError("Photo must be smaller than 5MB.");
      event.target.value = "";
      clearPhoto();
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const base64 = typeof result === "string" ? result.split(",").pop() : "";

      // Check encoded size (base64 adds ~33% overhead)
      const encodedBytes = base64.length * 0.75; // Approximate decoded size
      if (encodedBytes > MAX_PHOTO_BYTES) {
        setError("Photo is too large after encoding. Try reducing the image resolution or quality.");
        event.target.value = "";
        clearPhoto();
        return;
      }

      setPhotoState({
        preview: typeof result === "string" ? result : "",
        data: base64 || "",
        type: file.type,
      });
    };
    reader.onerror = () => {
      setError("Failed to read photo.");
      clearPhoto();
    };
    reader.readAsDataURL(file);
  };

  const startEditing = async (entry) => {
    if (!entry || entry.entryType === "team") return;
    setEditingId(entry.id || null);
    setForm({
      performedOn: entry.performedOn || todayStr(),
      workoutType: entry.workoutType || "erg",
      description: entry.description || "",
      durationMinutes:
        entry.durationMinutes != null && entry.durationMinutes !== ""
          ? String(entry.durationMinutes)
          : "",
      distanceMeters:
        entry.distanceMeters != null && entry.distanceMeters !== ""
          ? String(entry.distanceMeters)
          : "",
    });
    setPhotoInputKey((prev) => prev + 1);
    setError("");
    setSuccess("");
    setFormModalOpen(true);

    // Fetch the existing photo for preview if this workout has one.
    // The list endpoint no longer returns blob data to save server memory.
    if (entry.hasPhoto && entry.id) {
      try {
        const resp = await fetch(`${BASE_URL}/public/oyo-photos/${entry.id}`);
        if (resp.ok) {
          const blob = await resp.blob();
          const reader = new FileReader();
          reader.onload = (e) => setPhotoState({ preview: e.target.result || "", data: "", type: "" });
          reader.readAsDataURL(blob);
        } else {
          setPhotoState({ preview: "", data: "", type: "" });
        }
      } catch {
        setPhotoState({ preview: "", data: "", type: "" });
      }
    } else {
      setPhotoState({ preview: "", data: "", type: "" });
    }
  };

  async function handleSubmit(event) {
    event.preventDefault();
    if (saving || !userId) return;
    if (!form.description.trim()) {
      setError("Please describe the workout.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const durationValue = form.durationMinutes === "" ? null : Number(form.durationMinutes);
      if (durationValue !== null && !Number.isFinite(durationValue)) {
        setError("Duration must be a number.");
        setSaving(false);
        return;
      }
      const distanceValue = form.distanceMeters === "" ? null : Number(form.distanceMeters);
      if (distanceValue !== null && !Number.isFinite(distanceValue)) {
        setError("Distance must be a number.");
        setSaving(false);
        return;
      }

      const payload = {
        performedOn: form.performedOn || todayStr(),
        workoutType: form.workoutType,
        description: form.description.trim(),
        durationMinutes: durationValue,
        distanceMeters: distanceValue,
      };

      // Always include photo fields in payload for proper backend handling
      if (photoState.data && photoState.type) {
        // New photo uploaded
        payload.photoData = photoState.data;
        payload.photoType = photoState.type;
      } else if (!photoState.preview) {
        // No photo (either removed or never had one)
        payload.photoData = "";
        payload.photoType = "";
      }
      // If photoState.preview exists but no new data, don't send photo fields
      // This tells backend to preserve existing photo

      if (editingId) payload.id = editingId;

      console.log("Saving workout with payload:", {
        ...payload,
        photoData: payload.photoData ? `[base64 data, length: ${payload.photoData.length}]` : payload.photoData
      });

      const saved = await apiFetch(`/oyo-workouts/mine?as=${userId}`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      console.log("Received saved workout:", {
        ...saved,
        photoDataUrl: saved?.photoDataUrl ? `[data URL, length: ${saved.photoDataUrl.length}]` : saved?.photoDataUrl,
        hasPhoto: saved?.hasPhoto
      });

      if (saved) {
        const normalizedSaved = normalizeOyoEntries([saved])[0] || null;
        setEntries((prev) => {
          if (!normalizedSaved) return prev;
          const next = editingId
            ? prev.map((entry) =>
                entry.entryType === "oyo" && entry.id === normalizedSaved.id ? normalizedSaved : entry
              )
            : [normalizedSaved, ...prev];
          return sortOyoEntries(next).slice(0, fetchLimit);
        });
        resetForm();
        setFormModalOpen(false);
        setSuccess(editingId ? "Workout updated!" : "Workout logged!");
      } else {
        await loadEntries();
      }
    } catch (e) {
      console.error("Failed to save OYO workout", e);
      setError(e?.message || "Failed to save workout");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card oyoCard">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="h2" style={{ margin: 0 }}>My Workouts</h3>
        {userId && (
          <button className="btn" type="button" onClick={() => { resetForm(); setError(""); setSuccess(""); setFormModalOpen(true); }}>
            Log workout
          </button>
        )}
      </div>

      {!userId && (
        <p className="help" style={{ marginTop: 8 }}>Sign in as an athlete to log workouts.</p>
      )}

      <div className="oyoEntries">
   

        {formModalOpen && (
          <Modal onClose={() => { setFormModalOpen(false); resetForm(); }}>
            <div className="modalCardContent oyoModalContent">
              <div className="oyoModalHeader">
                <div>
                  <div className="oyoModalEyebrow">On Your Own</div>
                  <h3 className="oyoModalTitle">
                    {editingId ? "Edit workout" : "Log a workout"}
                  </h3>
                </div>
                <button className="modal-close" aria-label="Close" onClick={() => { setFormModalOpen(false); resetForm(); }}>
                  &times;
                </button>
              </div>
              <form className="oyoForm oyoFormSleek" onSubmit={handleSubmit}>
                <label className="oyoField">
                  <span>Workout type</span>
                  <div className="oyoTypePills">
                    {TYPE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`oyoTypePill${form.workoutType === opt.value ? " active" : ""}`}
                        onClick={() => updateForm({ workoutType: opt.value })}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </label>

                <label className="oyoField">
                  <span>Date</span>
                  <input
                    className="input"
                    type="date"
                    value={form.performedOn}
                    onChange={(e) => updateForm({ performedOn: e.target.value })}
                    max={todayStr()}
                    required
                  />
                </label>

                <label className="oyoField">
                  <span>What did you do?</span>
                  <textarea
                    className="input"
                    rows={3}
                    value={form.description}
                    onChange={(e) => updateForm({ description: e.target.value })}
                    placeholder="Example: 3x20' steady state at 20 spm"
                    required
                  />
                </label>

                <div className="oyoFieldRow">
                  <label className="oyoField">
                    <span>Duration (minutes)</span>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      inputMode="numeric"
                      value={form.durationMinutes}
                      onChange={(e) => updateForm({ durationMinutes: e.target.value })}
                      placeholder="Optional"
                    />
                  </label>
                  <label className="oyoField">
                    <span>Distance (meters)</span>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      inputMode="numeric"
                      value={form.distanceMeters}
                      onChange={(e) => updateForm({ distanceMeters: e.target.value })}
                      placeholder="Optional"
                    />
                  </label>
                </div>

                <label className="oyoField">
                  <span>Photo (optional)</span>
                  {photoState.preview ? (
                    <div className="oyoPhotoPreview">
                      <img src={photoState.preview} alt="Selected workout" />
                      <button
                        className="btn ghost small pill"
                        type="button"
                        onClick={clearPhoto}
                      >
                        Remove photo
                      </button>
                    </div>
                  ) : (
                    <div className="oyoPhotoDropzone">
                      <input
                        key={photoInputKey}
                        className="oyoPhotoInput"
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handlePhotoChange}
                      />
                      <span className="oyoPhotoDropzoneLabel">Tap to add a photo</span>
                      <span className="oyoPhotoDropzoneHint">JPG, PNG, GIF or WebP · up to 5MB</span>
                    </div>
                  )}
                </label>

                {error && (
                  <p className="help" style={{ color: "var(--danger)" }}>{error}</p>
                )}
                {success && (
                  <p className="help" style={{ color: "var(--success)" }}>{success}</p>
                )}

                <button className="btn oyoSubmitBtn" type="submit" disabled={saving}>
                  {saving ? "Saving…" : editingId ? "Update workout" : "Save workout"}
                </button>
              </form>
            </div>
          </Modal>
        )}

        {loading && <p className="help" style={{ marginTop: 8 }}>Loading…</p>}
        {!loading && userId && entries.length === 0 && (
          <p className="help" style={{ marginTop: 8 }}>No workouts logged yet.</p>
        )}
        {!userId && (
          <p className="help" style={{ marginTop: 8 }}>Sign in to view your history.</p>
        )}
        <div className="oyoEntryList">
          {entries.map((entry) => {
            const isTeamRecord = entry.entryType === "team";
            const entryClasses = ["oyoEntry"];
            if (!isTeamRecord) entryClasses.push("oyoEntryButton");
            if (isTeamRecord) entryClasses.push("teamWorkout");
            const entryProps = {};
            if (!isTeamRecord) {
              entryProps.role = "button";
              entryProps.tabIndex = 0;
              entryProps.onClick = () => {
                startEditing(entry);
              };
              entryProps.onKeyDown = (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  startEditing(entry);
                }
              };
            }
            return (
              <article
                key={entry.id || `${entry.entryType}-${entry.performedOn}-${entry.description}`}
                className={entryClasses.join(" ")}
                {...entryProps}
              >
                <div className="oyoEntryHeader">
                  <div>
                    <div className="oyoEntryDate">{formatDate(entry.performedOn)}</div>
                    <div className="oyoEntryDescription">{entry.description}</div>
                  </div>
                  <div className="oyoEntryActions">
                    <span className={`tag ${isTeamRecord ? "published" : "info"}`}>
                      {isTeamRecord
                        ? "Team workout"
                        : typeLabelByValue.get(entry.workoutType) || formatType(entry.workoutType)}
                    </span>
                  
                  </div>
                </div>
                {isTeamRecord ? (
                  <div className="oyoEntryMeta teamMeta">
                    <span><strong>Result:</strong> {formatTeamResult(entry)}</span>
                    {entry.targetDistanceMeters != null && (
                      <span><strong>Target:</strong> {`${entry.targetDistanceMeters} m`}</span>
                    )}
                    {entry.targetTimeSeconds != null && (
                      <span><strong>Target:</strong> {formatSeconds(entry.targetTimeSeconds)}</span>
                    )}
                    {entry.splitSeconds != null && (
                      <span><strong>Split:</strong> {formatSeconds(entry.splitSeconds)}</span>
                    )}
                    {entry.watts != null && (
                      <span><strong>Watts:</strong> {entry.watts}</span>
                    )}
                    {entry.practiceTime && (
                      <span><strong>Time:</strong> {entry.practiceTime}</span>
                    )}
                    <span className="help" style={{ margin: 0 }}>
                      Logged {formatRelative(entry.createdAt)}
                    </span>
                  </div>
                ) : (
                  <div className="oyoEntryMeta">
                    {entry.durationMinutes != null && entry.durationMinutes !== "" && (
                      <span>{entry.durationMinutes} min</span>
                    )}
                    {entry.distanceMeters != null && entry.distanceMeters !== "" && (
                      <span>{formatMeters(entry.distanceMeters)}</span>
                    )}
                    <span className="help" style={{ margin: 0 }}>
                      Logged {formatRelative(entry.createdAt)}
                    </span>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function todayStr() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function formatDate(value) {
  const date = parseDate(value);
  if (!date) return "—";
  return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function formatType(value) {
  if (!value) return "OYO";
  return String(value).split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}

function formatMeters(value) {
  if (value == null) return "";
  return `${Number(value).toLocaleString()} m`;
}

function formatRelative(dateStr) {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffMinutes = Math.round(diffMs / 60000);
    if (diffMinutes < 60) {
      return `${diffMinutes || 1} min ago`;
    }
    const diffHours = Math.round(diffMinutes / 60);
    if (diffHours < 24) {
      return `${diffHours} hr${diffHours === 1 ? "" : "s"} ago`;
    }
    const diffDays = Math.round(diffHours / 24);
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  } catch {
    return "";
  }
}

function sortOyoEntries(list = []) {
  return [...list].sort((a, b) => {
    const aPerformed = parseDate(a?.performedOn);
    const bPerformed = parseDate(b?.performedOn);
    const performedDiff =
      (bPerformed ? bPerformed.getTime() : 0) - (aPerformed ? aPerformed.getTime() : 0);
    if (performedDiff !== 0) return performedDiff;
    const aCreated = parseDate(a?.createdAt);
    const bCreated = parseDate(b?.createdAt);
    const createdDiff =
      (bCreated ? bCreated.getTime() : 0) - (aCreated ? aCreated.getTime() : 0);
    if (createdDiff !== 0) return createdDiff;
    return String(b?.id || "").localeCompare(String(a?.id || ""));
  });
}

function parseDate(input) {
  if (!input) return null;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  const str = String(input).trim();
  if (!str) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const date = new Date(`${str}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeOyoEntries(list = []) {
  return list.map((entry) => ({
    entryType: "oyo",
    ...entry,
  }));
}

function normalizeTeamResults(list = []) {
  return list.map((row) => ({
    entryType: "team",
    id: `team-${row.resultId || row.workoutId || Math.random()}`,
    performedOn: row.practiceDate || row.recordedAt || null,
    description: row.workoutName
      ? `${row.workoutName}${row.pieceNumber ? ` • Piece ${row.pieceNumber}` : ""}`
      : "Team workout",
    workoutType: row.workoutType || "erg",
    createdAt: row.recordedAt || null,
    resultDistanceMeters: row.resultDistanceMeters ?? null,
    resultTimeSeconds: row.resultTimeSeconds ?? null,
    splitSeconds: row.splitSeconds ?? null,
    watts: row.watts ?? null,
    targetDistanceMeters: row.targetDistanceMeters ?? null,
    targetTimeSeconds: row.targetTimeSeconds ?? null,
    practiceTime: row.practiceTime || null,
  }));
}

function formatTeamResult(entry) {
  const parts = [];
  if (entry.resultTimeSeconds != null) {
    parts.push(formatSeconds(entry.resultTimeSeconds));
  }
  if (entry.resultDistanceMeters != null) {
    parts.push(`${Number(entry.resultDistanceMeters).toLocaleString()} m`);
  }
  return parts.length ? parts.join(" / ") : "—";
}

function Modal({ children, onClose }) {
  return (
    <div className="modalOverlay" onClick={onClose}>
      <div className="modalCard" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
